import { useEffect, useMemo, useState } from 'react';
import { Copy, Eye, Package, Pencil, Plus, Search, Trash } from 'lucide-react';
import { DropiLinkButton } from './DropiLinkButton';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTableArea,
  CardTitle,
} from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import { Switch } from '@/components/ui/Switch';
import {
  Table,
  TableActionsCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import type { Product, ProductFilters } from '@/types/products';
import { ProductDetailDialog } from './ProductDetailDialog';
import { ProductDialog } from './ProductDialog';
import { ProductDropiDialog } from './ProductDropiDialog';
import { useDuplicateProduct } from './use-duplicate-product';
import {
  PRODUCT_PAGE_SIZES,
  PRODUCT_PAGE_SIZE_DEFAULT,
  describeFaq,
  describeProductRange,
  describeOrderVariant,
  formatNumber,
  formatTierShort,
  inventorySummary,
  sortedPriceTiers,
  summarizeProductExtras,
  supportedProductLabel,
} from './products-format';
import { useDeleteProduct, useProducts, useUpdateProduct } from './use-products';

/**
 * Productos (`/products`, módulo 3): el catálogo del negocio activo.
 *
 * Esta pantalla es la que decide qué sabe el bot. El catálogo se serializa tal cual en el prompt
 * (`catalogo_de_productos`), así que un producto mal cargado se nota en la conversación: el bot
 * improvisa el precio, no sabe qué variantes preguntar o promete stock que no hay. Por eso cada
 * fila resume, además del nombre y el precio, lo que el producto "aporta" al prompt
 * (`summarizeProductExtras`: fotos, variantes, reglas, FAQs) y la ficha muestra todo completo.
 *
 * El backend NO acepta query params acá (`GET .../products` devuelve el catálogo entero, ordenado
 * por creación), así que la búsqueda y el filtro de estado se resuelven en el cliente sobre la
 * lista que ya está en caché: buscar es instantáneo y no hay ida y vuelta por cada tecla.
 *
 * Permisos: ver el catálogo lo puede hacer cualquier miembro; crear, editar, activar y borrar es
 * solo ADMIN (`canEdit`). El backend responde 403 igual si alguien invoca los endpoints a mano.
 */

/** Filtro de estado de la barra de herramientas (el estado es `activo`, un booleano). */
type StatusFilter = 'todos' | 'activos' | 'inactivos';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'todos', label: 'Todos los productos' },
  { value: 'activos', label: 'Solo activos' },
  { value: 'inactivos', label: 'Solo inactivos' },
];

/** Cuántos rangos de precio se listan en la celda antes de resumir el resto con "+N". */
const PRICE_TIERS_IN_TABLE = 2;

/** Retardo de la búsqueda: el servidor filtra por `buscar`, así que no se pide en cada tecla. */
const SEARCH_DEBOUNCE_MS = 400;

export function ProductsPage() {
  const { tenantId, canEdit } = useActiveTenantAccess();

  const [search, setSearch] = useState('');
  /** Lo que de verdad viaja al servidor: se actualiza 400 ms después de la última tecla. */
  const [buscar, setBuscar] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  /** Filtros por marca y categoría ('todas' = sin filtrar). Las opciones las da el servidor. */
  const [marcaFilter, setMarcaFilter] = useState('todas');
  const [categoriaFilter, setCategoriaFilter] = useState('todas');
  /** Página del catálogo: el servidor pagina (`limite`/`offset`), así que la tabla no es infinita. */
  const [limit, setLimit] = useState<number>(PRODUCT_PAGE_SIZE_DEFAULT);
  const [offset, setOffset] = useState(0);

  /**
   * Filtros y página que viajan al backend (los mismos que forman la query key). Cambiar cualquiera
   * devuelve la tabla a la primera página: un `offset` viejo sobre otro filtro no significa nada.
   */
  const filtros = useMemo<ProductFilters>(
    () => ({
      ...(buscar ? { buscar } : {}),
      ...(statusFilter === 'todos' ? {} : { activo: statusFilter === 'activos' }),
      ...(marcaFilter !== 'todas' ? { marca: marcaFilter } : {}),
      ...(categoriaFilter !== 'todas' ? { categoria: categoriaFilter } : {}),
      limite: limit,
      offset,
    }),
    [buscar, statusFilter, marcaFilter, categoriaFilter, limit, offset],
  );

  const productsQuery = useProducts(tenantId, filtros);
  const updateProduct = useUpdateProduct(tenantId);
  const deleteProduct = useDeleteProduct(tenantId);
  const duplicateProduct = useDuplicateProduct(tenantId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  /** Producto cuya ficha está abierta (`null` = ficha cerrada). */
  const [detail, setDetail] = useState<Product | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Product | null>(null);
  /** Producto cuyo vínculo con Dropi se está editando (`null` = diálogo cerrado). */
  const [dropiProduct, setDropiProduct] = useState<Product | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  /** La página que se pinta + los números y las opciones de filtro que devuelve el servidor. */
  const products = productsQuery.data?.items ?? [];
  const total = productsQuery.data?.total ?? 0;
  const marcas = productsQuery.data?.marcas ?? [];
  const categorias = productsQuery.data?.categorias ?? [];

  /**
   * La búsqueda se manda con RETARDO (mismo patrón que el Inbox): ahora filtra el servidor, así que no
   * se pide una vez por tecla. Mientras el texto escrito todavía no viajó, la tabla sigue mostrando los
   * resultados anteriores (`keepPreviousData`).
   */
  useEffect(() => {
    if (search.trim() === buscar) return undefined;

    const timer = setTimeout(() => {
      setBuscar(search.trim());
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search, buscar]);

  /**
   * La tabla ya no filtra: lo hace el servidor (`buscar`/`activo`/`marca`/`categoria`) y acá solo quedan
   * los tres estados que la pantalla distingue —hay catálogo y coincidencias, hay catálogo pero nada
   * coincide, o esta página quedó vacía— y la paginación.
   */
  const filtering =
    buscar.length > 0 ||
    statusFilter !== 'todos' ||
    marcaFilter !== 'todas' ||
    categoriaFilter !== 'todas';
  /** Hay catálogo, pero nada pasa los filtros: se distingue de "todavía no hay productos". */
  const noMatches = total === 0 && filtering;
  /** El catálogo tiene productos, pero ESTA página quedó vacía (se borraron los suyos). */
  const emptyPage = total > 0 && products.length === 0;
  const hayAnterior = offset > 0;
  const haySiguiente = offset + products.length < total;

  /** Cambia un filtro (o el tamaño de página) y vuelve a la primera página. */
  function handleFiltro(cambios: {
    statusFilter?: StatusFilter;
    marcaFilter?: string;
    categoriaFilter?: string;
    limit?: number;
  }) {
    if (cambios.statusFilter !== undefined) setStatusFilter(cambios.statusFilter);
    if (cambios.marcaFilter !== undefined) setMarcaFilter(cambios.marcaFilter);
    if (cambios.categoriaFilter !== undefined) setCategoriaFilter(cambios.categoriaFilter);
    if (cambios.limit !== undefined) setLimit(cambios.limit);
    setOffset(0);
  }

  function openCreate() {
    setEditing(null);
    setActionError(null);
    setDialogOpen(true);
  }

  function openEdit(product: Product) {
    setEditing(product);
    setActionError(null);
    setDialogOpen(true);
  }

  function clearFilters() {
    setSearch('');
    setBuscar('');
    setStatusFilter('todos');
    setMarcaFilter('todas');
    setCategoriaFilter('todas');
    setOffset(0);
  }

  /** Activar/desactivar sin abrir el modal: un `PATCH` con un solo campo (`activo`). */
  async function toggleActive(product: Product) {
    setActionError(null);
    try {
      await updateProduct.mutateAsync({
        productId: product.id,
        payload: { activo: !product.activo },
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;

    setActionError(null);
    try {
      await deleteProduct.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          actions={
            canEdit ? (
              <Button size="sm" leftIcon={<Plus size={14} />} onClick={openCreate}>
                Nuevo producto
              </Button>
            ) : null
          }
        >
          <CardTitle>Catálogo</CardTitle>
          <CardDescription>
            {total === 0
              ? 'Sin productos'
              : `${formatNumber(total)} ${total === 1 ? 'producto' : 'productos'} en el catálogo`}
            . Lo que se guarda acá es lo que el bot puede decir (y
            cobrar) en la conversación: precios por cantidad, variantes que pregunta, reglas,
            respuestas y fotos. Solo los productos activos entran al prompt.
            {canEdit
              ? ''
              : ' Tu rol en este negocio es de solo lectura: solo un administrador puede crear, editar o borrar productos.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {actionError ? <Alert>{actionError}</Alert> : null}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              type="search"
              value={search}
              leftIcon={<Search size={16} />}
              placeholder="Buscar por nombre, marca, categoría o descripción…"
              aria-label="Buscar productos"
              onChange={(event) => setSearch(event.target.value)}
            />

            <Select
              aria-label="Filtrar por estado"
              className="sm:w-52"
              value={statusFilter}
              onChange={(event) =>
                handleFiltro({ statusFilter: event.target.value as StatusFilter })
              }
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            {/* Los filtros de marca y categoría solo aparecen si el catálogo los tiene: en un negocio
                de un solo producto serían ruido. */}
            {marcas.length > 0 ? (
              <Select
                aria-label="Filtrar por marca"
                className="sm:w-44"
                value={marcaFilter}
                onChange={(event) => handleFiltro({ marcaFilter: event.target.value })}
              >
                <option value="todas">Todas las marcas</option>
                {marcas.map((marca) => (
                  <option key={marca} value={marca}>
                    {marca}
                  </option>
                ))}
              </Select>
            ) : null}

            {categorias.length > 0 ? (
              <Select
                aria-label="Filtrar por categoría"
                className="sm:w-44"
                value={categoriaFilter}
                onChange={(event) => handleFiltro({ categoriaFilter: event.target.value })}
              >
                <option value="todas">Todas las categorías</option>
                {categorias.map((categoria) => (
                  <option key={categoria} value={categoria}>
                    {categoria}
                  </option>
                ))}
              </Select>
            ) : null}

            {/* Cuántos productos por página: el valor viaja como `limite` al servidor (25 por defecto). */}
            <Select
              aria-label="Productos por página"
              className="sm:w-40"
              value={String(limit)}
              onChange={(event) => handleFiltro({ limit: Number(event.target.value) })}
            >
              {PRODUCT_PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size} por página
                </option>
              ))}
            </Select>
          </div>

          {productsQuery.isPending ? <PageLoader label="Cargando el catálogo…" /> : null}

          {productsQuery.isError ? <Alert>{getErrorMessage(productsQuery.error)}</Alert> : null}

          {productsQuery.isSuccess ? (
            <>
              {!filtering && total === 0 ? (
                <EmptyState
                  icon={<Package size={22} />}
                  title="Todavía no hay productos"
                  description="Agrega el primer producto con su nombre y su tabla de precios: es lo mínimo que el bot necesita para vender. Después puedes sumarle variantes, reglas, preguntas frecuentes y fotos."
                  action={
                    canEdit ? (
                      <Button leftIcon={<Plus size={16} />} onClick={openCreate}>
                        Crear el primer producto
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}

              {noMatches ? (
                <EmptyState
                  icon={<Search size={22} />}
                  title="Ningún producto coincide"
                  description={
                    search.trim().length > 0
                      ? `Nada en el catálogo coincide con «${search.trim()}»${
                          statusFilter === 'todos'
                            ? ''
                            : statusFilter === 'activos'
                              ? ' entre los productos activos'
                              : ' entre los productos inactivos'
                        }.`
                      : 'No hay productos que cumplan ese filtro de estado.'
                  }
                  action={
                    <Button variant="secondary" onClick={clearFilters}>
                      Quitar los filtros
                    </Button>
                  }
                />
              ) : null}

              {emptyPage ? (
                <EmptyState
                  icon={<Package size={22} />}
                  title="Esta página quedó vacía"
                  description="El catálogo cambió mientras tenías esta página abierta: los productos que había acá se borraron o se desactivaron. Volvé a la primera página para ver el catálogo con los números de ahora."
                  action={
                    <Button variant="secondary" onClick={() => setOffset(0)}>
                      Volver a la primera página
                    </Button>
                  }
                />
              ) : null}

              {products.length > 0 ? (
                <CardTableArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Producto</TableHeaderCell>
                        <TableHeaderCell align="right">Precio por cantidad</TableHeaderCell>
                        <TableHeaderCell>Inventario</TableHeaderCell>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell align="right">Acciones</TableHeaderCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {products.map((product) => {
                        const tiers = sortedPriceTiers(product.tablaPrecios);
                        const extras = summarizeProductExtras(product);

                        return (
                          <TableRow key={product.id}>
                            <TableCell>
                              <span className="flex max-w-md flex-col gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => setDetail(product)}
                                  className="text-left text-body font-medium text-ink transition-colors hover:text-brand-600"
                                >
                                  {product.nombre}
                                </button>

                                {product.descripcion ? (
                                  <span className="line-clamp-2 text-caption text-ink-muted">
                                    {product.descripcion}
                                  </span>
                                ) : null}

                                {product.marca || product.categoria ? (
                                  <span className="mt-0.5 flex flex-wrap items-center gap-1">
                                    {product.marca ? <Badge tone="brand">{product.marca}</Badge> : null}
                                    {product.categoria ? (
                                      <Badge tone="neutral">{product.categoria}</Badge>
                                    ) : null}
                                  </span>
                                ) : null}

                                <span className="mt-0.5 text-micro text-ink-faint">
                                  {extras.length > 0
                                    ? extras.join(' · ')
                                    : 'Solo nombre y precio: el bot no tiene nada más que decir de este producto.'}
                                </span>
                              </span>
                            </TableCell>

                            <TableCell align="right">
                              <span className="flex flex-col items-end gap-0.5">
                                {tiers.length === 0 ? (
                                  <span className="text-caption text-ink-faint">
                                    Sin rango de precio
                                  </span>
                                ) : (
                                  tiers.slice(0, PRICE_TIERS_IN_TABLE).map((tier) => (
                                    <span
                                      key={tier.cantidad}
                                      className="tabular text-caption text-ink-soft"
                                    >
                                      {formatTierShort(tier)}
                                    </span>
                                  ))
                                )}

                                {tiers.length > PRICE_TIERS_IN_TABLE ? (
                                  <span className="text-micro text-ink-faint">
                                    {supportedProductLabel(
                                      tiers.length - PRICE_TIERS_IN_TABLE,
                                      'rango más',
                                      'rangos más',
                                    )}
                                  </span>
                                ) : null}
                              </span>
                            </TableCell>

                            <TableCell>
                              {(() => {
                                const inventory = inventorySummary(product);

                                return inventory.soldOut ? (
                                  <Badge tone="danger" dot>
                                    Agotado
                                  </Badge>
                                ) : (
                                  <span
                                    className="tabular text-caption text-ink-soft"
                                    title={inventory.hint}
                                  >
                                    {inventory.label}
                                  </span>
                                );
                              })()}
                            </TableCell>

                            <TableCell>
                              <span className="flex items-center gap-2.5">
                                <Switch
                                  checked={product.activo}
                                  label={`Activar o desactivar ${product.nombre}`}
                                  disabled={!canEdit || updateProduct.isPending}
                                  onChange={() => void toggleActive(product)}
                                />
                                <Badge tone={product.activo ? 'success' : 'neutral'}>
                                  {product.activo ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </span>
                            </TableCell>

                            <TableActionsCell>
                              <span className="inline-flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Ver la ficha de ${product.nombre}`}
                                  onClick={() => setDetail(product)}
                                >
                                  <Eye size={16} />
                                </Button>

                                {canEdit ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={`Editar ${product.nombre}`}
                                      onClick={() => openEdit(product)}
                                    >
                                      <Pencil size={16} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={`Duplicar ${product.nombre}`}
                                      title="Duplica el producto (copia precios, variantes y reglas; las fotos van vacías y nace inactivo)"
                                      disabled={duplicateProduct.isPending}
                                      onClick={() => {
                                        void duplicateProduct
                                          .mutateAsync({ productId: product.id })
                                          .then((copia) => openEdit(copia))
                                          .catch((error) =>
                                            setActionError(getErrorMessage(error)),
                                          );
                                      }}
                                    >
                                      <Copy size={16} />
                                    </Button>
                                    <DropiLinkButton
                                      product={product}
                                      onVincular={() => setDropiProduct(product)}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={`Eliminar ${product.nombre}`}
                                      onClick={() => {
                                        setActionError(null);
                                        setPendingDelete(product);
                                      }}
                                    >
                                      <Trash size={16} />
                                    </Button>
                                  </>
                                ) : null}
                              </span>
                            </TableActionsCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardTableArea>
              ) : null}

              {/* Paginación: el servidor devuelve una página por vez (`limite`/`offset`) y el total de
                  lo que coincide con los filtros, así que acá no hay scroll infinito. */}
              {total > 0 && !emptyPage ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-caption text-ink-faint">
                    {describeProductRange(offset, products.length, total)}
                    {filtering ? ' · con los filtros aplicados' : ''}
                  </p>

                  <span className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!hayAnterior || productsQuery.isFetching}
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                    >
                      Anterior
                    </Button>

                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!haySiguiente || productsQuery.isFetching}
                      onClick={() => setOffset(offset + limit)}
                    >
                      Siguiente
                    </Button>
                  </span>
                </div>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      {canEdit ? (
        <ProductDialog open={dialogOpen} onClose={() => setDialogOpen(false)} product={editing} />
      ) : null}

      {/* Ficha de solo lectura: se abre con el nombre o con el ojo de la fila. El detalle se
          vuelve a leer del endpoint (`useProduct`) y el botón de editar solo aparece si el
          usuario es ADMIN. */}
      <ProductDetailDialog
        open={detail !== null}
        product={detail}
        onClose={() => setDetail(null)}
        onEdit={
          canEdit
            ? (product) => {
                setDetail(null);
                openEdit(product);
              }
            : undefined
        }
      />

      {/* Confirmación de borrado: el error del backend se muestra DENTRO del diálogo para que el
          usuario no pierda el contexto de lo que estaba haciendo. */}
      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Eliminar el producto"
        description={
          pendingDelete
            ? `Se va a eliminar «${pendingDelete.nombre}» del catálogo y el bot dejará de ofrecerlo de inmediato. Si solo quieres dejar de venderlo un tiempo, desactívalo: se conserva con sus precios, variantes, reglas y fotos.`
            : ''
        }
        confirmLabel="Eliminar producto"
        loading={deleteProduct.isPending}
        error={actionError}
      />

      {/* Vínculo con Dropi (docs/DROPI.md): solo ADMIN, porque cambia lo que se le envía al
          proveedor cuando la orden se manda a Dropi. */}
      {canEdit ? (
        <ProductDropiDialog
          open={dropiProduct !== null}
          product={dropiProduct}
          onClose={() => setDropiProduct(null)}
        />
      ) : null}
    </>
  );
}

